import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tempo-io/sui-dialog';
import { CheckCircle, ErrorIndicator, Info, Warning } from '@tempo-io/sui-icons';
import type { AriaAttributes, ReactNode } from 'react';

type DialogSize = 'sm' | 'md' | 'lg';

type DialogIntent = 'default' | 'success' | 'warning' | 'info' | 'destructive';

export interface BaseDialogFooterAction {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  aria?: AriaAttributes;
  'data-testid'?: string;
}

export interface BaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  size?: DialogSize;
  intent?: DialogIntent;
  primaryAction?: BaseDialogFooterAction;
  secondaryAction?: BaseDialogFooterAction;
  additionalAction?: BaseDialogFooterAction;
  isPending?: boolean;
  showCloseButton?: boolean;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  className?: string;
  'data-testid'?: string;
}

const SIZE_CLASSES: Record<DialogSize, string> = {
  sm: 'max-w-[425px]! w-[calc(100%-64px)]! max-h-[calc(100%-64px)]!',
  md: 'max-w-[600px]! w-[calc(100%-64px)]! max-h-[calc(100%-64px)]!',
  lg: 'max-w-[1000px]! w-[calc(100%-64px)]! max-h-[calc(100%-64px)]!',
};

const INTENT_ICONS: Partial<Record<DialogIntent, ReactNode>> = {
  success: <CheckCircle className="size-5 shrink-0 text-text-success" />,
  warning: <Warning className="size-5 shrink-0 text-text-warning-bold" />,
  info: <Info className="size-5 shrink-0 text-text-info-bold" />,
  destructive: <ErrorIndicator className="size-5 shrink-0 text-text-error" />,
};

export const BaseDialog = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = 'md',
  intent = 'default',
  primaryAction,
  secondaryAction,
  additionalAction,
  isPending,
  showCloseButton,
  onEscapeKeyDown,
  className,
  'data-testid': dataTestId,
}: BaseDialogProps) => {
  const icon = INTENT_ICONS[intent];
  const hasFooter = primaryAction ?? secondaryAction ?? additionalAction;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      if (primaryAction && !primaryAction.disabled && !isPending) {
        primaryAction.onClick();
      }
    }
  };

  const stopKeyBubble = (e: React.KeyboardEvent) => e.stopPropagation();

  return (
    /* biome-ignore lint/a11y/noStaticElementInteractions: Stops React-tree-bubbled key events from reaching outside of
       the modal (e.g. MUI DataGrid edit cells). Radix internal handlers fire first so Escape close + focus trap +
       Cmd+Enter still work without unexpected interactions elsewhere. */
    <div onKeyDown={stopKeyBubble} onKeyUp={stopKeyBubble} style={{ display: 'contents' }}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={showCloseButton}
          onEscapeKeyDown={onEscapeKeyDown}
          onKeyDown={handleKeyDown}
          className={cn(SIZE_CLASSES[size], className)}
          data-testid={dataTestId}
        >
          <DialogHeader>
            <DialogTitle className={cn(icon && 'flex items-center gap-2')}>
              {icon}
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription asChild>
                <div>{description}</div>
              </DialogDescription>
            )}
          </DialogHeader>
          {children}
          {hasFooter && (
            <DialogFooter className={cn(additionalAction && 'sm:justify-between')}>
              {additionalAction && (
                <Button
                  variant="outline"
                  type="button"
                  onClick={additionalAction.onClick}
                  disabled={additionalAction.disabled ?? isPending}
                  data-testid={additionalAction['data-testid']}
                >
                  {additionalAction.label}
                </Button>
              )}
              <div className="flex gap-2">
                {secondaryAction && (
                  <Button
                    variant="outline"
                    type="button"
                    onClick={secondaryAction.onClick}
                    disabled={secondaryAction.disabled ?? isPending}
                    data-testid={secondaryAction['data-testid']}
                    {...secondaryAction.aria}
                  >
                    {secondaryAction.label}
                  </Button>
                )}
                {primaryAction && (
                  <Button
                    type="button"
                    variant={intent === 'destructive' ? 'destructive' : 'default'}
                    onClick={primaryAction.onClick}
                    disabled={primaryAction.disabled ?? isPending}
                    data-testid={primaryAction['data-testid']}
                    {...primaryAction.aria}
                  >
                    {primaryAction.label}
                  </Button>
                )}
              </div>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
